
const libPictProvider = require('pict-provider');

// Read-only diagnostics: the recent Headlight WEB error log. The field/portal apps POST client-side errors
// as `Artifact` rows of ArtifactType 'HLWebErrorLog' (the error JSON lives in the parsed `Datum` column:
// Datum.Error.{message,stack,cause} + Datum.Payload.{name,hash,IDProject,IDUser}). This page loads the last
// ~30 days, charts them per day, and lists them in a filterable table with an expandable JSON payload.
// Reads only — no writes. Mirrors the bespoke-page pattern (provider derives AppData, view paints).

const WINDOW_DAYS = 30;
const FETCH_CAP = 2000;
const TABLE_CAP = 200;

class ErrorLogViewerProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
		this._rows = [];
		this._loaded = false;
		this._appFilter = 'all';
		this._textFilter = '';
		this._expanded = {};
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	// --- Loading ---

	_sinceDate()
	{
		const tmpDate = new Date(Date.now() - (WINDOW_DAYS - 1) * 24 * 3600 * 1000);
		return tmpDate.toISOString().slice(0, 10);
	}

	async loadAsync()
	{
		const tmpState = this.viewState;
		tmpState.Loading = true;
		tmpState.Message = '';
		this._render();
		try
		{
			const tmpSince = this._sinceDate();
			const tmpRows = await this._getJSON(`Artifacts/FilteredTo/FBV~ArtifactType~EQ~HLWebErrorLog~FBV~CreateDate~GE~${tmpSince}/0/${FETCH_CAP}`);
			this._rows = (Array.isArray(tmpRows) ? tmpRows : []).map((pRow) => this._parseRow(pRow)).filter((pRow) => pRow);
			// Newest first.
			this._rows.sort((a, b) => b.Time - a.Time);
			this._loaded = true;
			tmpState.Loading = false;
			this._render();
		}
		catch (pError)
		{
			this._loaded = true;
			tmpState.Loading = false;
			tmpState.Message = `Could not load the error log: ${pError.message || pError}. You may not have permission to read diagnostic artifacts.`;
			tmpState.MessageClass = 'is-warning';
			this._render();
		}
	}

	reload()
	{
		this._expanded = {};
		this.loadAsync();
	}

	/** Normalize an Artifact row into a flat display record (Datum may be an object OR a JSON string). */
	_parseRow(pRow)
	{
		let tmpDatum = pRow.Datum;
		if (typeof tmpDatum === 'string')
		{
			try { tmpDatum = JSON.parse(tmpDatum); }
			catch (pError) { tmpDatum = null; }
		}
		const tmpError = (tmpDatum && tmpDatum.Error) || {};
		const tmpPayload = (tmpDatum && tmpDatum.Payload) || {};
		const tmpTime = new Date(pRow.CreateDate);
		return {
			ID: pRow.IDArtifact,
			Time: Number.isNaN(tmpTime.getTime()) ? 0 : tmpTime.getTime(),
			DateRaw: pRow.CreateDate,
			DayKey: this._localDayKey(tmpTime),
			App: String(tmpPayload.name || tmpPayload.hash || '(unknown)'),
			Message: String(tmpError.message || '(no message)'),
			Stack: tmpError.stack ? String(tmpError.stack) : '',
			Cause: (tmpError.cause != null) ? String(tmpError.cause) : '',
			IDProject: tmpPayload.IDProject != null ? tmpPayload.IDProject : null,
			IDUser: (tmpPayload.IDUser != null) ? tmpPayload.IDUser : pRow.CreatingIDUser,
			Datum: tmpDatum,
		};
	}

	_localDayKey(pDate)
	{
		if (!(pDate instanceof Date) || Number.isNaN(pDate.getTime())) { return ''; }
		const tmpYear = pDate.getFullYear();
		const tmpMonth = String(pDate.getMonth() + 1).padStart(2, '0');
		const tmpDay = String(pDate.getDate()).padStart(2, '0');
		return `${tmpYear}-${tmpMonth}-${tmpDay}`;
	}

	// --- Filtering ---

	get apps()
	{
		const tmpCounts = {};
		this._rows.forEach((pRow) => { tmpCounts[pRow.App] = (tmpCounts[pRow.App] || 0) + 1; });
		return Object.keys(tmpCounts).sort((a, b) => tmpCounts[b] - tmpCounts[a]);
	}

	/** Rows after the app filter only (drives the chart + summary). */
	_appFilteredRows()
	{
		if (this._appFilter === 'all') { return this._rows; }
		return this._rows.filter((pRow) => pRow.App === this._appFilter);
	}

	/** Rows after app + free-text filter (drives the table). */
	_filteredRows()
	{
		const tmpText = this._textFilter.trim().toLowerCase();
		return this._appFilteredRows().filter((pRow) =>
		{
			if (!tmpText) { return true; }
			return (pRow.Message.toLowerCase().indexOf(tmpText) >= 0)
				|| (pRow.App.toLowerCase().indexOf(tmpText) >= 0)
				|| (pRow.Stack.toLowerCase().indexOf(tmpText) >= 0);
		});
	}

	setAppFilter(pApp)
	{
		this._appFilter = pApp || 'all';
		this._render();
	}

	/** Free-text filter — repaint ONLY the table host so the input keeps focus + caret. */
	setTextFilter(pValue)
	{
		this._textFilter = pValue || '';
		this.deriveRenderState();
		const tmpView = this.pict.views.ErrorLogViewer;
		if (tmpView && (typeof tmpView.syncTable === 'function')) { tmpView.syncTable(); }
	}

	toggleRow(pID)
	{
		if (this._expanded[pID]) { delete this._expanded[pID]; }
		else { this._expanded[pID] = true; }
		this.deriveRenderState();
		const tmpView = this.pict.views.ErrorLogViewer;
		if (tmpView && (typeof tmpView.syncTable === 'function')) { tmpView.syncTable(); }
	}

	// --- Display formatting ---

	_formatTime(pRow)
	{
		if (!pRow.Time) { return '—'; }
		const tmpDate = new Date(pRow.Time);
		return tmpDate.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
	}

	_appClass(pApp)
	{
		switch (pApp)
		{
			case 'fieldbook': return 'elv-app-fieldbook';
			case 'portal': return 'elv-app-portal';
			case 'tracker': return 'elv-app-tracker';
			default: return 'elv-app-unknown';
		}
	}

	_shortDayLabel(pDayKey)
	{
		// pDayKey is 'YYYY-MM-DD'; render as 'Jun 3' from the parts (no tz shift).
		const tmpParts = String(pDayKey).split('-');
		if (tmpParts.length !== 3) { return pDayKey; }
		const tmpMonths = [ 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec' ];
		return `${tmpMonths[Number(tmpParts[1]) - 1] || ''} ${Number(tmpParts[2])}`;
	}

	// --- Render-state ---

	get viewState()
	{
		if (!this.pict.AppData.ErrorLogViewer)
		{
			this.pict.AppData.ErrorLogViewer =
			{
				Loading: false, Message: '', MessageClass: '',
				LoadingSlot: [], MessageSlot: [], BodySlot: [],
				WindowLabel: `Last ${WINDOW_DAYS} days`,
				Total: 0, AppChips: [], FilterButtons: [], TextFilter: '',
				Bars: [], AxisLabels: [], ChartMaxLabel: '',
				Rows: [], TableEmptySlot: [], TableCountLabel: '',
			};
		}
		return this.pict.AppData.ErrorLogViewer;
	}

	deriveRenderState()
	{
		const tmpState = this.viewState;
		tmpState.MessageSlot = tmpState.Message ? [ { Message: tmpState.Message, MessageClass: tmpState.MessageClass || 'is-info' } ] : [];
		tmpState.WindowLabel = `Last ${WINDOW_DAYS} days`;
		tmpState.TextFilter = this._textFilter;

		if (tmpState.Loading)
		{
			tmpState.LoadingSlot = [ {} ];
			tmpState.BodySlot = [];
			return;
		}
		tmpState.LoadingSlot = [];
		tmpState.BodySlot = [ {} ];

		const tmpAppRows = this._appFilteredRows();
		tmpState.Total = tmpAppRows.length;

		// App filter buttons (All + each app) with counts.
		const tmpAppCounts = {};
		this._rows.forEach((pRow) => { tmpAppCounts[pRow.App] = (tmpAppCounts[pRow.App] || 0) + 1; });
		tmpState.FilterButtons = [ { Key: 'all', Label: 'All', Count: this._rows.length, ActiveClass: (this._appFilter === 'all') ? 'is-active' : '' } ]
			.concat(this.apps.map((pApp) => ({ Key: this._attrJsString(pApp), Label: this._escape(pApp), Count: tmpAppCounts[pApp], ActiveClass: (this._appFilter === pApp) ? 'is-active' : '' })));
		tmpState.AppChips = this.apps.map((pApp) => ({ Label: this._escape(pApp), Count: tmpAppCounts[pApp], AppClass: this._appClass(pApp) }));

		// Per-day bars across the window (continuous axis so gaps are visible).
		const tmpDayCounts = {};
		tmpAppRows.forEach((pRow) => { if (pRow.DayKey) { tmpDayCounts[pRow.DayKey] = (tmpDayCounts[pRow.DayKey] || 0) + 1; } });
		const tmpDays = [];
		for (let i = WINDOW_DAYS - 1; i >= 0; i--)
		{
			tmpDays.push(this._localDayKey(new Date(Date.now() - i * 24 * 3600 * 1000)));
		}
		const tmpMax = Math.max(1, ...tmpDays.map((pDay) => tmpDayCounts[pDay] || 0));
		tmpState.Bars = tmpDays.map((pDay, pIndex) =>
		{
			const tmpCount = tmpDayCounts[pDay] || 0;
			return {
				DayKey: pDay,
				Count: tmpCount,
				HeightPct: Math.round((tmpCount / tmpMax) * 100),
				Tooltip: `${this._shortDayLabel(pDay)}: ${tmpCount} error${tmpCount === 1 ? '' : 's'}`,
				MaxClass: (tmpCount === tmpMax && tmpCount > 0) ? 'is-max' : '',
				// Sparse axis labels (~every 6 days + the last day).
				AxisLabel: ((pIndex % 6 === 0) || (pIndex === WINDOW_DAYS - 1)) ? this._shortDayLabel(pDay) : '',
			};
		});
		tmpState.ChartMaxLabel = `peak ${tmpMax}/day`;

		// Table (capped; newest first).
		const tmpFiltered = this._filteredRows();
		const tmpShown = tmpFiltered.slice(0, TABLE_CAP);
		tmpState.Rows = tmpShown.map((pRow) =>
		{
			const tmpExpanded = !!this._expanded[pRow.ID];
			return {
				ID: pRow.ID,
				Time: this._formatTime(pRow),
				AppDisplay: this._escape(pRow.App),
				AppClass: this._appClass(pRow.App),
				Message: this._escape(pRow.Message),
				Context: this._escape(this._rowContext(pRow)),
				Chevron: tmpExpanded ? 'fa-chevron-down' : 'fa-chevron-right',
				RowClass: tmpExpanded ? 'is-expanded' : '',
				DetailSlot: tmpExpanded ? [ {
					Stack: this._escape(pRow.Stack || '(no stack trace)'),
					CauseSlot: pRow.Cause ? [ { Cause: this._escape(pRow.Cause) } ] : [],
					JSON: this._escape(this._safeJSON(pRow.Datum)),
				} ] : [],
			};
		});
		tmpState.TableEmptySlot = (tmpShown.length === 0) ? [ {} ] : [];
		tmpState.TableCountLabel = (tmpFiltered.length > tmpShown.length)
			? `Showing the first ${tmpShown.length} of ${tmpFiltered.length} matching errors`
			: `${tmpFiltered.length} error${tmpFiltered.length === 1 ? '' : 's'}`;
	}

	_rowContext(pRow)
	{
		const tmpParts = [];
		if (pRow.IDProject != null && pRow.IDProject !== 0) { tmpParts.push(`Project ${pRow.IDProject}`); }
		if (pRow.IDUser != null && pRow.IDUser !== 0) { tmpParts.push(`User ${pRow.IDUser}`); }
		return tmpParts.join(' · ') || '—';
	}

	_safeJSON(pValue)
	{
		try { return JSON.stringify(pValue, null, 2); }
		catch (pError) { return String(pValue); }
	}

	// Error-log payloads are CLIENT-submitted and routinely contain raw HTML (e.g. a serialized <select> in
	// the error cause). The pict `{~D:~}` macro inserts values as-is, so every error-derived string MUST be
	// HTML-escaped here before it reaches a template — both so the markup renders as literal text and so a
	// hostile payload can't inject script into this admin page.
	_escape(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
	}

	/** Escape a value that lands inside a single-quoted JS string inside a double-quoted HTML attribute. */
	_attrJsString(pValue)
	{
		const tmpJs = String(pValue == null ? '' : pValue).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
		return tmpJs.replace(/[&<>"]/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[pChar]));
	}

	_render()
	{
		this.deriveRenderState();
		if (this.pict.views.ErrorLogViewer)
		{
			this.pict.views.ErrorLogViewer.render();
		}
	}
}

module.exports = ErrorLogViewerProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'ErrorLogViewer',
	AutoInitialize: true,
	AutoInitializeOrdinal: 223,
};
